import React, { useCallback, useContext, useEffect, useState } from 'react'
import dynamic from 'next/dynamic'
import cookie from 'cookie'
import { GlobalStoreContext } from 'Components/store'
import { checkLogin, getSsoSDK, fetchUserInfo } from 'New/login/service'
import { LOGIN_STORAGE_KEY } from 'New/login/utils/constants'
import { LoginStorageValues } from 'New/login/utils/page-control'
import Toast from 'New/side-nav/components/feedback-modal/custom-toast'
import { PageType } from 'New/login/index'
import { teaConfig } from 'lib/tea-analyze'
import {
  ILoginCommonParams,
  ILoginTeaParams,
} from 'New/login/context/view-context'
import useWindowLoaded from 'Components/hooks/use-window-loaded'

const Login = dynamic(() => import('New/login/index'), {
  ssr: false,
})

export const LoginStoreContext = React.createContext(
  {} as {
    openLogin(commonParams?: ILoginCommonParams): void
    logout(): void
  }
)

const saveUserData = (userData: string) => {
  document.cookie = `user_data=${encodeURIComponent(userData)};max-age=${
    60 * 60 * 24 * 30
  };path=/`
}

const clearUserData = () => {
  document.cookie = 'user_data=;max-age=-1;path=/'
}

const teaStoreKey = 'login_tea_params'

const getLoginTeaParams = () => {
  let teaParams: ILoginTeaParams = {}
  try {
    const str = sessionStorage.getItem(teaStoreKey)
    if (str) {
      teaParams = JSON.parse(str)
    }
  } catch (e) {
    // noop
  }
  return teaParams
}

const saveLoginTeaParams = (teaParams?: ILoginTeaParams) => {
  try {
    if (teaParams) {
      sessionStorage.setItem(teaStoreKey, JSON.stringify(teaParams))
    }
  } catch (e) {
    // noop
  }
}

const LoginStoreProvider: React.FC = ({ children }) => {
  const [showLogin, setShowLogin] = useState(false)

  const { dispatch } = useContext(GlobalStoreContext)

  useEffect(() => {
    const cookies = cookie.parse(document.cookie)
    try {
      const userData = JSON.parse(cookies.user_data)
      dispatch({
        type: 'setUserData',
        payload: userData,
      })
    } catch (e) {
      //
    }
  }, [])

  const autoLogin = useCallback(() => {
    try {
      if (localStorage.getItem('is_auto_login')) {
        localStorage.removeItem('is_auto_login')
        return
      }
    } catch {
      // noop
    }
    getSsoSDK()
      .login.checkLogin({
        service: window.location.href,
      })
      .then((res) => {
        if (res?.has_login) {
          Toast.success(
            {
              title: '正在登录，自动为用户登录成功',
              isModal: false,
              getContainer: () => document.body,
            },
            2000
          )
          localStorage.setItem('is_auto_login', '1')
          window.location.href = res.redirect_url
        }
      })
  }, [])

  const [teaParams, setTeaParams] = useState<ILoginTeaParams>({})
  const [pageType, setPageType] = useState<PageType>('LOGIN_MOBILE_CODE')

  const openLogin = useCallback(
    ({
      autoCheck = true,
      pageType = 'LOGIN_MOBILE_CODE',
      ...teaParams
    }: ILoginCommonParams = {}) => {
      setTeaParams(teaParams)
      setShowLogin(true)
      setPageType(pageType)
      if (autoCheck) {
        autoLogin()
      }
      saveLoginTeaParams(teaParams)
    },
    [autoLogin]
  )

  const logout = useCallback(() => {
    clearUserData()
    teaConfig({
      user_is_login: false,
    })
    getSsoSDK().login.logout({
      service: `${
        window.location.origin
      }/passport/web/logout/?next=${encodeURIComponent(window.location.href)}`,
    })
  }, [])

  // 判断是否需要设置密码
  const checkNeedSetPassword = useCallback(() => {
    checkLogin()
      .then((res) => {
        if (res?.has_password !== 1) {
          openLogin({
            autoCheck: false,
            pageType: 'SET_PWD',
            ...getLoginTeaParams(),
          })
        }
      })
      .catch(() => {
        //
      })
  }, [])

  const checkNeedBindMobile = useCallback(() => {
    checkLogin()
      .then((res) => {
        if (res?.mobile === '') {
          openLogin({
            autoCheck: false,
            pageType: 'BIND_MOBILE',
            ...getLoginTeaParams(),
          })
        }
      })
      .catch(() => {
        //
      })
  }, [openLogin])

  const fetchUserData = useCallback(() => {
    // 通过接口获取用户数据，cookies的用户可能登录过期
    fetchUserInfo()
      .then((res) => {
        const loginType = localStorage.getItem(
          LOGIN_STORAGE_KEY
        ) as LoginStorageValues
        localStorage.removeItem(LOGIN_STORAGE_KEY)
        if (res) {
          dispatch({
            type: 'setUserData',
            payload: res,
          })
          saveUserData(JSON.stringify(res))
          teaConfig({
            user_is_login: true,
          })
          const successTip = () => {
            Toast.success(
              {
                title: '登录成功',
                isModal: false,
                getContainer: () => document.body,
              },
              2000
            )
          }
          switch (loginType) {
            case LoginStorageValues.MOBILE_CODE_LOGIN:
              checkNeedSetPassword()
              successTip()
              break
            case LoginStorageValues.AUTH_LOGIN:
              checkNeedBindMobile()
              successTip()
              break
            case LoginStorageValues.ACCOUNT_LOGIN:
              successTip()
              break
          }
        } else {
          clearUserData()
          teaConfig({
            user_is_login: false,
          })
          autoLogin()
        }
      })
      .catch(() => {
        // 如果没有登录则清除用户数据
        clearUserData()
        teaConfig({
          user_is_login: false,
        })
        autoLogin()
      })
  }, [dispatch])

  useEffect(() => {
    fetchUserData()
  }, [fetchUserData])

  const handleSuccess = useCallback(() => {
    fetchUserData()
  }, [fetchUserData])

  return (
    <LoginStoreContext.Provider
      value={{
        openLogin,
        logout,
      }}
    >
      {children}
      {showLogin ? (
        <Login
          onClose={() => setShowLogin(false)}
          onSuccess={handleSuccess}
          pageType={pageType}
          teaParams={teaParams}
        />
      ) : null}
    </LoginStoreContext.Provider>
  )
}

export default LoginStoreProvider
